import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKnowledgeBaseArticles, KnowledgeBaseArticle, useKnowledgeBaseCategories, KnowledgeBaseCategory, deleteKnowledgeBaseArticle } from './useData';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { moduleConfig } from './config';
import { Button, TextInput, Select, TableViewIcon, TableEditIcon, TableDeleteIcon } from '../../components/ui';
import { ConfirmationDialog } from '../../components/ui';
import { formatDate as formatDateGlobal } from '../../config/appConfig';

/** Custom KB home layout (not GenericListPage) — persist category search/sort/expand when navigating to edit/article. */
const KB_HOME_UI_KEY = `${getListStateStorageKey(moduleConfig.module.route.list)}:home-ui`;

function loadKbHomeUiState(): {
  expandedCategories: Set<number>;
  categorySearchTerms: Record<number, string>;
  categoryShowAll: Record<number, boolean>;
  categorySortBy: Record<number, 'title' | 'updated' | 'views'>;
} | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KB_HOME_UI_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as {
      expandedIds?: number[];
      categorySearchTerms?: Record<string, string>;
      categoryShowAll?: Record<string, boolean>;
      categorySortBy?: Record<string, 'title' | 'updated' | 'views'>;
    };
    const numRecord = <V,>(rec: Record<string, V> | undefined): Record<number, V> => {
      if (!rec || typeof rec !== 'object') return {};
      return Object.fromEntries(
        Object.entries(rec).map(([k, v]) => [Number(k), v])
      ) as Record<number, V>;
    };
    return {
      expandedCategories: new Set(Array.isArray(p.expandedIds) ? p.expandedIds : []),
      categorySearchTerms: numRecord(p.categorySearchTerms),
      categoryShowAll: numRecord(p.categoryShowAll),
      categorySortBy: numRecord(p.categorySortBy),
    };
  } catch {
    return null;
  }
}

function persistKbHomeUiState(state: {
  expandedCategories: Set<number>;
  categorySearchTerms: Record<number, string>;
  categoryShowAll: Record<number, boolean>;
  categorySortBy: Record<number, 'title' | 'updated' | 'views'>;
}): void {
  try {
    sessionStorage.setItem(
      KB_HOME_UI_KEY,
      JSON.stringify({
        expandedIds: Array.from(state.expandedCategories),
        categorySearchTerms: state.categorySearchTerms,
        categoryShowAll: state.categoryShowAll,
        categorySortBy: state.categorySortBy,
      })
    );
  } catch {
    // ignore
  }
}

const List: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showNotification } = useNotification();
  const initialKb = loadKbHomeUiState();
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    () => initialKb?.expandedCategories ?? new Set()
  );
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [categorySearchTerms, setCategorySearchTerms] = useState<Record<number, string>>(
    () => initialKb?.categorySearchTerms ?? {}
  );
  const [categoryShowAll, setCategoryShowAll] = useState<Record<number, boolean>>(
    () => initialKb?.categoryShowAll ?? {}
  );
  const [categorySortBy, setCategorySortBy] = useState<Record<number, 'title' | 'updated' | 'views'>>(
    () => initialKb?.categorySortBy ?? {}
  );
  const [articlesPerPage] = useState(10);

  useEffect(() => {
    persistKbHomeUiState({
      expandedCategories,
      categorySearchTerms,
      categoryShowAll,
      categorySortBy,
    });
  }, [expandedCategories, categorySearchTerms, categoryShowAll, categorySortBy]);
  
  const { data: categories, isLoading: categoriesLoading, mutate: mutateCategories } = useKnowledgeBaseCategories();
  const { data: articles, isLoading: articlesLoading, mutate: mutateArticles } = useKnowledgeBaseArticles();

  // Group articles by category
  const articlesByCategory = useMemo(() => {
    const grouped: Record<number, KnowledgeBaseArticle[]> = {};
    const uncategorized: KnowledgeBaseArticle[] = [];
    
    (articles || []).forEach((article) => {
      if (article.categoryId) {
        if (!grouped[article.categoryId]) {
          grouped[article.categoryId] = [];
        }
        grouped[article.categoryId].push(article);
      } else {
        uncategorized.push(article);
      }
    });
    
    return { grouped, uncategorized };
  }, [articles]);

  // Get active categories sorted by displayOrder
  const activeCategories = useMemo(() => {
    return (categories || [])
      .filter(cat => cat.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [categories]);

  const toggleCategory = (categoryId: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleDeleteArticle = async (articleId: number) => {
    try {
      await deleteKnowledgeBaseArticle(articleId);
      showNotification('Article deleted successfully', 'success');
      setDeleteConfirm(null);
      mutateArticles();
      mutateCategories();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to delete article', 'error');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      published: { bg: colors.success, text: '', label: 'Published' },
      draft: { bg: colors.warning, text: '', label: 'Draft' },
      archived: { bg: colors.surfaceHover, text: colors.textSecondary, label: 'Archived' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    return (
      <span className={cn('px-2 py-0.5 text-xs font-semibold rounded-full', config.bg, config.text)}>
        {config.label}
      </span>
    );
  };

  // Filter and sort articles for a category
  const getFilteredAndSortedArticles = (categoryId: number, articles: KnowledgeBaseArticle[]) => {
    let filtered = [...articles];
    
    // Apply search filter
    const searchTerm = categorySearchTerms[categoryId]?.toLowerCase() || '';
    if (searchTerm) {
      filtered = filtered.filter(article => 
        article.title.toLowerCase().includes(searchTerm) ||
        article.summary?.toLowerCase().includes(searchTerm) ||
        article.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }
    
    // Apply sorting
    const sortBy = categorySortBy[categoryId] || 'updated';
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'views':
          return (b.viewCount || 0) - (a.viewCount || 0);
        case 'updated':
        default:
          const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return dateB - dateA;
      }
    });
    
    return filtered;
  };

  // Get displayed articles (with pagination)
  const getDisplayedArticles = (categoryId: number, articles: KnowledgeBaseArticle[]) => {
    const filtered = getFilteredAndSortedArticles(categoryId, articles);
    const showAll = categoryShowAll[categoryId];
    
    if (showAll || filtered.length <= articlesPerPage) {
      return filtered;
    }
    
    return filtered.slice(0, articlesPerPage);
  };

  if (categoriesLoading || articlesLoading) {
    return (
      <div className={cn('flex flex-col h-full', colors.background)}>
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className={cn('inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mb-4', colors.primary)} />
            <div className={cn('font-medium', colors.textSecondary)}>Loading knowledge base...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full min-h-0', colors.background)}>
      {/* Content (header handled by MainLayout) */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Actions row: manage categories / new article */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/knowledge-base/categories')}
              variant="default"
              className={cn('h-10 px-4 py-2.5 text-sm font-medium rounded-lg border transition-all duration-200 shadow-sm hover:shadow-md inline-flex items-center gap-2', colors.primary, colors.primaryText, colors.primaryBorderAll, colors.primaryHover)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0-01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0-01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0-01-1-1v-6z"
                />
              </svg>
              <span>Manage Categories</span>
            </Button>
            <Button
              onClick={() => navigate('/knowledge-base/new')}
              variant="default"
              className={cn('px-6 py-2.5 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-300', colors.primary, colors.primaryText, colors.primaryHover)}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Article
            </Button>
          </div>
        </div>
        {/* Empty State */}
        {!categoriesLoading && !articlesLoading && activeCategories.length === 0 && articlesByCategory.uncategorized.length === 0 && (
          <div className="flex flex-col items-center justify-center h-96">
            <div className="relative mb-8">
              <div className={cn('w-32 h-32 rounded-full flex items-center justify-center shadow-xl', colors.info)}>
                <svg className={cn('w-16 h-16', colors.primaryFg)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full animate-pulse"></div>
            </div>
            <h3 className={cn('text-2xl font-bold mb-2', colors.text)}>No categories yet</h3>
            <p className={cn('text-center mb-8 max-w-md', colors.textMuted)}>Create your first category to start organizing knowledge base articles</p>
            <Button 
              onClick={() => navigate('/knowledge-base/categories/new')} 
              variant="default"
              className={cn('px-8 py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300', colors.primary, colors.primaryText, colors.primaryHover)}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Your First Category
            </Button>
          </div>
        )}

        {/* Categories Grid */}
        {activeCategories.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {activeCategories.map((category, index) => {
              const isExpanded = expandedCategories.has(category.id);
              const categoryArticles = articlesByCategory.grouped[category.id] || [];
              const categoryColor = category.color || '#6366F1';
              
              return (
                <div
                  key={category.id}
                  className={cn('group relative backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border overflow-hidden', colors.surface, colors.border)}
                  style={{
                    animationDelay: `${index * 50}ms`,
                  }}
                >
                  {/* Gradient Accent Bar */}
                  <div 
                    className="h-1.5 w-full"
                    style={{ 
                      background: `linear-gradient(90deg, ${categoryColor} 0%, ${categoryColor}dd 50%, ${categoryColor}99 100%)` 
                    }}
                  />

                  {/* Category Header */}
                  <div
                    className="p-6 cursor-pointer"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Icon with Gradient Background */}
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl shadow-lg transform group-hover:scale-110 transition-transform duration-300"
                          style={{ 
                            background: `linear-gradient(135deg, ${categoryColor} 0%, ${categoryColor}dd 100%)`,
                            boxShadow: `0 10px 25px -5px ${categoryColor}40`
                          }}
                        >
                          {category.icon || '📚'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={cn('text-xl font-bold mb-1 transition-colors group-hover:opacity-90', colors.text)}>
                            {category.name}
                          </h3>
                          {category.description && (
                            <p className={cn('text-sm line-clamp-2', colors.textMuted)}>{category.description}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(category.id);
                        }}
                        className={cn('p-2 rounded-lg transition-colors', colors.interactiveHover, colors.textMuted, colors.text)}
                      >
                        <svg
                          className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-4">
                      <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg', colors.info)}>
                        <div className={cn('w-2 h-2 rounded-full', colors.primary)}></div>
                        <span className={cn('text-sm font-semibold', colors.text)}>{categoryArticles.length}</span>
                        <span className={cn('text-xs', colors.textMuted)}>articles</span>
                      </div>
                      {category._count && (
                        <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg', colors.surfaceHover)}>
                          <span className={cn('text-sm font-semibold', colors.textSecondary)}>{category._count.articles}</span>
                          <span className={cn('text-xs', colors.textMuted)}>total</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions Bar */}
                  <div className={cn('px-6 pb-4 flex gap-2 border-t', colors.border)}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/knowledge-base/new', { state: { categoryId: category.id } });
                      }}
                      variant="ghost"
                      size="sm"
                      className={cn('flex-1 font-medium', colors.primaryFg, colors.interactiveHover)}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Article
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/knowledge-base/categories/${category.id}/edit`);
                      }}
                      variant="ghost"
                      size="icon"
                      className={cn(colors.primaryFg, colors.interactiveHover)}
                    >
                      <TableEditIcon />
                    </Button>
                  </div>

                  {/* Expanded Content */}
                  <div 
                    className={cn('overflow-hidden transition-all duration-500 ease-in-out',
                      isExpanded ? (categoryShowAll[category.id] ? 'max-h-none opacity-100' : 'max-h-[2000px] opacity-100') : 'max-h-0 opacity-0'
                    )}
                  >
                    <div className={cn('border-t', colors.border, colors.surface)}>
                      <div className="p-5">
                        {/* Search and Sort Controls */}
                        <div className="mb-4 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <TextInput
                                type="text"
                                placeholder="Search articles..."
                                value={categorySearchTerms[category.id] || ''}
                                onChange={(e) => setCategorySearchTerms({ ...categorySearchTerms, [category.id]: e.target.value })}
                                className="w-full"
                              />
                            </div>
                            <Select
                              value={categorySortBy[category.id] || 'updated'}
                              onChange={(e) => setCategorySortBy({ ...categorySortBy, [category.id]: e.target.value as 'title' | 'updated' | 'views' })}
                              className="w-40"
                              options={[
                                { value: 'updated', label: 'Recently Updated' },
                                { value: 'title', label: 'Title (A-Z)' },
                                { value: 'views', label: 'Most Viewed' },
                              ]}
                            />
                          </div>
                        </div>

                        {/* Articles Header */}
                        <div className="flex items-center justify-between mb-4">
                          <h4 className={cn('text-sm font-bold flex items-center gap-2', colors.text)}>
                            <div className={cn('w-1.5 h-1.5 rounded-full', colors.primary)}></div>
                            Articles
                            <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', colors.info)}>
                              {(() => {
                                const filtered = getFilteredAndSortedArticles(category.id, categoryArticles);
                                return `${filtered.length}${categorySearchTerms[category.id] ? ` of ${categoryArticles.length}` : ''}`;
                              })()}
                            </span>
                          </h4>
                        </div>

                        {(() => {
                          const filteredArticles = getFilteredAndSortedArticles(category.id, categoryArticles);
                          const displayedArticles = getDisplayedArticles(category.id, categoryArticles);
                          const showAll = categoryShowAll[category.id];
                          const hasMore = filteredArticles.length > articlesPerPage;

                          if (filteredArticles.length === 0) {
                            return (
                              <div className="text-center py-8">
                                <div className={cn('w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center', colors.surfaceHover)}>
                                  <svg className={cn('w-8 h-8', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <p className={cn('text-sm', colors.textMuted)}>
                                  {categorySearchTerms[category.id] 
                                    ? 'No articles found matching your search' 
                                    : 'No articles in this category'}
                                </p>
                              </div>
                            );
                          }

                          return (
                            <>
                              <div className="space-y-3">
                                {displayedArticles.map((article) => (
                              <div
                                key={article.id}
                                onClick={() => navigate(`/knowledge-base/${article.id}`)}
                                className={cn('rounded-xl border hover:shadow-lg transition-all duration-300 group cursor-pointer overflow-hidden', colors.surface, colors.border)}
                              >
                                {/* Article Header with Status Bar */}
                                <div className="relative">
                                  <div className={cn(
                                    'h-1 w-full',
                                    article.status === 'published' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                                    article.status === 'draft' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
                                    'bg-gradient-to-r from-gray-400 to-gray-500'
                                  )} />
                                  <div className="absolute top-2 right-2 flex items-center gap-1.5 z-10">
                                    {article.isPinned && (
                                      <span className={cn('p-1.5 rounded-lg shadow-sm', colors.warning)} title="Pinned">
                                        <svg className={cn('w-4 h-4', colors.text)} fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M5.5 17.5a.5.5 0 01-1 0v-11a.5.5 0 011 0v11zM9.293 1.293a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 3.414V13.5a.5.5 0 01-1 0V3.414L7.707 5.707a1 1 0 01-1.414-1.414l3-3z" />
                                        </svg>
                                      </span>
                                    )}
                                    {article.isFeatured && (
                                      <span className={cn('p-1.5 rounded-lg shadow-sm', colors.info)} title="Featured">
                                        <svg className={cn('w-4 h-4', colors.primaryFg)} fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Article Content */}
                                <div className="p-5">
                                  <div className="flex items-start gap-4">
                                    {/* Article Icon */}
                                    <div className={cn('flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300', colors.info)}>
                                      <svg className={cn('w-6 h-6', colors.primaryFg)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>

                                    {/* Main Content */}
                                    <div className="flex-1 min-w-0">
                                      {/* Title and Status */}
                                      <div className="flex items-start justify-between gap-3 mb-2">
                                        <h3 className={cn('text-lg font-bold transition-colors line-clamp-2', colors.text)}>
                                          {article.title}
                                        </h3>
                                        {getStatusBadge(article.status)}
                                      </div>

                                      {/* Summary */}
                                      {article.summary && (
                                        <p className={cn('text-sm line-clamp-2 mb-3 leading-relaxed', colors.textMuted)}>
                                          {article.summary}
                                        </p>
                                      )}

                                      {/* Metadata Row */}
                                      <div className="flex items-center flex-wrap gap-3 mb-3">
                                        {(() => {
                                          const attachmentCount = article.attachments?.length ?? article._count?.attachments ?? 0;
                                          return attachmentCount > 0 ? (
                                            <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg', colors.surfaceHover)}>
                                              <svg className={cn('w-3.5 h-3.5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a2 2 0 00-2.828-2.828l-6.414 6.414a4 4 0 11-5.656-5.656l6.586-6.586a4 4 0 015.656 5.656l-6.586 6.586a6 6 0 11-8.485-8.485l6.586-6.586" />
                                              </svg>
                                              <span className={cn('text-xs font-medium', colors.textSecondary)}>{attachmentCount}</span>
                                              <span className={cn('text-xs', colors.textMuted)}>file{attachmentCount !== 1 ? 's' : ''}</span>
                                            </div>
                                          ) : null;
                                        })()}
                                        {article.viewCount > 0 && (
                                          <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg', colors.surfaceHover)}>
                                            <svg className={cn('w-3.5 h-3.5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            <span className={cn('text-xs font-medium', colors.textSecondary)}>{article.viewCount}</span>
                                            <span className={cn('text-xs', colors.textMuted)}>views</span>
                                          </div>
                                        )}
                                        {article.updatedAt && (
                                          <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg', colors.surfaceHover)}>
                                            <svg className={cn('w-3.5 h-3.5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className={cn('text-xs', colors.textMuted)}>{formatDateGlobal(article.updatedAt)}</span>
                                          </div>
                                        )}
                                        {article.createdBy && (
                                          <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg', colors.surfaceHover)}>
                                            <svg className={cn('w-3.5 h-3.5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            <span className={cn('text-xs truncate max-w-[100px]', colors.textMuted)}>
                                              {article.createdBy.userDisplayName || 
                                               `${article.createdBy.userFirstName || ''} ${article.createdBy.userLastName || ''}`.trim() || 
                                               'Unknown'}
                                            </span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Tags */}
                                      {article.tags && article.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-3">
                                          {article.tags.slice(0, 4).map((tag, idx) => (
                                            <span
                                              key={idx}
                                              className={cn('px-2.5 py-1 text-xs font-medium rounded-md border', colors.info, colors.primaryBorder)}
                                            >
                                              {tag}
                                            </span>
                                          ))}
                                          {article.tags.length > 4 && (
                                            <span className={cn('px-2.5 py-1 text-xs font-medium', colors.textMuted)}>
                                              +{article.tags.length - 4} more
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className={cn('flex items-center justify-end gap-2 mt-4 pt-4 border-t', colors.border)}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/knowledge-base/${article.id}`);
                                      }}
                                      className={cn('px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5', colors.primaryFg, colors.interactiveHover)}
                                    >
                                      <TableViewIcon />
                                      Read
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/knowledge-base/${article.id}/edit`);
                                      }}
                                      className={cn('px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5', colors.primaryFg, colors.interactiveHover)}
                                    >
                                      <TableEditIcon />
                                      Edit
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteConfirm(article.id);
                                      }}
                                      className={cn('px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5', colors.error)}
                                    >
                                      <TableDeleteIcon />
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                                ))}
                              </div>
                              
                              {/* Show More/Less Button */}
                              {hasMore && (
                                <div className="flex justify-center pt-4">
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCategoryShowAll({ ...categoryShowAll, [category.id]: !showAll });
                                    }}
                                    variant="ghost"
                                    className={cn(colors.primaryFg, colors.interactiveHover)}
                                  >
                                    {showAll ? (
                                      <>
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                        Show Less ({articlesPerPage} shown)
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                        Show All ({filteredArticles.length} articles)
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Uncategorized Articles */}
        {articlesByCategory.uncategorized.length > 0 && (
          <div className="mt-6">
            <div className={cn('group relative backdrop-blur-sm rounded-2xl shadow-lg border overflow-hidden', colors.surface, colors.border)}>
              <div className={cn('h-1.5 w-full', colors.surfaceHover)} />
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className={cn('w-14 h-14 rounded-xl flex items-center justify-center text-2xl shadow-lg', colors.surfaceHover)}>
                    📄
                  </div>
                  <div className="flex-1">
                    <h3 className={cn('text-xl font-bold mb-1', colors.text)}>
                      Uncategorized
                    </h3>
                    <p className={cn('text-sm', colors.textMuted)}>Articles without a category</p>
                  </div>
                  <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg', colors.surfaceHover)}>
                    <div className={cn('w-2 h-2 rounded-full', colors.primary)}></div>
                    <span className={cn('text-sm font-semibold', colors.textSecondary)}>{articlesByCategory.uncategorized.length}</span>
                    <span className={cn('text-xs', colors.textMuted)}>articles</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {articlesByCategory.uncategorized.map((article) => (
                    <div
                      key={article.id}
                      onClick={() => navigate(`/knowledge-base/${article.id}`)}
                      className={cn('rounded-xl border hover:shadow-lg transition-all duration-300 group cursor-pointer overflow-hidden', colors.surface, colors.border)}
                    >
                      {/* Status Bar */}
                      <div className={cn(
                        'h-1 w-full',
                        article.status === 'published' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                        article.status === 'draft' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
                        'bg-gradient-to-r from-gray-400 to-gray-500'
                      )} />

                      {/* Article Content */}
                      <div className="p-5">
                        <div className="flex items-start gap-4">
                          {/* Article Icon */}
                          <div className={cn('flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300', colors.surfaceHover)}>
                            <svg className={cn('w-6 h-6', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>

                          {/* Main Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <h3 className={cn('text-lg font-bold transition-colors line-clamp-2', colors.text)}>
                                {article.title}
                              </h3>
                              {getStatusBadge(article.status)}
                            </div>
                            {article.summary && (
                              <p className={cn('text-sm line-clamp-2 mb-3 leading-relaxed', colors.textMuted)}>
                                {article.summary}
                              </p>
                            )}
                            <div className="flex items-center flex-wrap gap-3">
                              {(() => {
                                const attachmentCount = article.attachments?.length ?? article._count?.attachments ?? 0;
                                return attachmentCount > 0 ? (
                                  <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg', colors.surfaceHover)}>
                                    <svg className={cn('w-3.5 h-3.5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a2 2 0 00-2.828-2.828l-6.414 6.414a4 4 0 11-5.656-5.656l6.586-6.586a4 4 0 015.656 5.656l-6.586 6.586a6 6 0 11-8.485-8.485l6.586-6.586" />
                                    </svg>
                                    <span className={cn('text-xs font-medium', colors.textSecondary)}>{attachmentCount}</span>
                                    <span className={cn('text-xs', colors.textMuted)}>file{attachmentCount !== 1 ? 's' : ''}</span>
                                  </div>
                                ) : null;
                              })()}
                              {article.viewCount > 0 && (
                                <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg', colors.surfaceHover)}>
                                  <svg className={cn('w-3.5 h-3.5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  <span className={cn('text-xs font-medium', colors.textSecondary)}>{article.viewCount}</span>
                                  <span className={cn('text-xs', colors.textMuted)}>views</span>
                                </div>
                              )}
                              {article.updatedAt && (
                                <div className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-lg', colors.surfaceHover)}>
                                  <svg className={cn('w-3.5 h-3.5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className={cn('text-xs', colors.textMuted)}>{formatDateGlobal(article.updatedAt)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className={cn('flex items-center justify-end gap-2 mt-4 pt-4 border-t', colors.border)}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/knowledge-base/${article.id}`);
                            }}
                            className={cn('px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5', colors.primaryFg, colors.interactiveHover)}
                          >
                            <TableViewIcon />
                            Read
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/knowledge-base/${article.id}/edit`);
                            }}
                            className={cn('px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5', colors.primaryFg, colors.interactiveHover)}
                          >
                            <TableEditIcon />
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            handleDeleteArticle(deleteConfirm);
          }
        }}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
      />
    </div>
  );
};

export default List;
